// Global media stream registry to track and stop MediaStreams created via getUserMedia
// Ensures components can stop only the streams they spawned without affecting others.

interface RegistryStreamEntry {
  stream: MediaStream;
  createdAt: number; // performance.now timestamp
}

interface AcornMediaRegistry {
  originalGetUserMedia: typeof navigator.mediaDevices.getUserMedia;
  patched: boolean;
  refCount: number;
  streams: RegistryStreamEntry[];
}

declare global {
  interface Window {
    __ACORN_MEDIA_REGISTRY__?: AcornMediaRegistry;
  }
}

function ensureRegistry(): AcornMediaRegistry | null {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return null;
  if (!window.__ACORN_MEDIA_REGISTRY__) {
    window.__ACORN_MEDIA_REGISTRY__ = {
      originalGetUserMedia: navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices),
      patched: false,
      refCount: 0,
      streams: [],
    };
  }
  return window.__ACORN_MEDIA_REGISTRY__!;
}

export function acquireMediaRegistry() {
  const registry = ensureRegistry();
  if (!registry) return null;
  registry.refCount++;
  if (!registry.patched) {
    const reg = registry;
    navigator.mediaDevices.getUserMedia = async function (constraints: MediaStreamConstraints) {
      const stream = await reg.originalGetUserMedia(constraints);
      const entry: RegistryStreamEntry = { stream, createdAt: performance.now() };
      reg.streams.push(entry);
      // Clean up ended streams automatically
      stream.getTracks().forEach(track => {
        track.addEventListener('ended', () => {
          // Remove entry if all tracks ended
          if (stream.getTracks().every(t => t.readyState === 'ended')) {
            reg.streams = reg.streams.filter(e => e.stream !== stream);
          }
        });
      });
      return stream;
    } as any;
    registry.patched = true;
  }
  return {
    stopNewStreamsSince(since: number) {
      if (!registry) return;
      const toStop = registry.streams.filter(e => e.createdAt >= since);
      toStop.forEach(e => {
        e.stream.getTracks().forEach(t => { if (t.readyState === 'live') t.stop(); });
      });
      // Prune stopped streams
      registry.streams = registry.streams.filter(e => e.stream.getTracks().some(t => t.readyState === 'live'));
    }
  };
}

export function releaseMediaRegistry() {
  const registry = window.__ACORN_MEDIA_REGISTRY__;
  if (!registry) return;
  registry.refCount = Math.max(0, registry.refCount - 1);
  if (registry.refCount === 0 && registry.patched) {
    // Restore original
    navigator.mediaDevices.getUserMedia = registry.originalGetUserMedia;
    // Stop any lingering streams (safety)
    registry.streams.forEach(e => {
      e.stream.getTracks().forEach(t => { if (t.readyState === 'live') t.stop(); });
    });
    registry.streams = [];
    registry.patched = false;
  }
}

export function stopStreamsSince(timestamp: number) {
  const registry = window.__ACORN_MEDIA_REGISTRY__;
  if (!registry) return;
  registry.streams.filter(e => e.createdAt >= timestamp).forEach(e => {
    e.stream.getTracks().forEach(t => { if (t.readyState === 'live') t.stop(); });
  });
  // Prune
  registry.streams = registry.streams.filter(e => e.stream.getTracks().some(t => t.readyState === 'live'));
}
